import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { PlusCircle, Upload, Loader, X } from "lucide-react";
import { useProductStore } from "../stores/useProductStore";
import axios from "../lib/axios";

const categories = ["necklaces", "earrings", "bracelets", "rings"];

const CreateProductForm = () => {
	const [newProduct, setNewProduct] = useState({
		name: "",
		description: "",
		price: "",
		category: "",
		images: [], // שינוי מ-image ל-images
		// שדות חדשים
		sku: "",
		material: "",
		weight: "",
		length: "", // אורך בסנטימטר
		earringThickness: "",
		wireThickness: "",
		stockLocation: "",
		imageStatus: "pending",
		productStatus: "active",
		gender: "",
		stoneType: "",
		isNewProduct: false,
		isOnSale: false,
		inStock: true,
		isFeatured: false,
		quantity: "",
		originalPrice: "",
	});

	const { createProduct, loading, fetchAllProducts } = useProductStore();

	// פונקציה לקבלת ה-SKU הבא בתור
	const getNextSKU = async (category) => {
		try {
			const response = await axios.get(`/products/next-sku/${category}`);
			return response.data.nextSKU;
		} catch (error) {
			console.error("Error getting next SKU:", error);
			return "";
		}
	};

	// עדכון SKU אוטומטי כשהקטגוריה משתנה
	useEffect(() => {
		if (newProduct.category) {
			getNextSKU(newProduct.category).then(nextSKU => {
				setNewProduct(prev => ({ ...prev, sku: nextSKU }));
			});
		}
	}, [newProduct.category]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			await createProduct(newProduct);
			// רענון רשימת המוצרים אחרי יצירה
			await fetchAllProducts();
			setNewProduct({ 
				name: "", 
				description: "", 
				price: "", 
				category: "", 
				images: [],
				sku: "",
				material: "",
				weight: "",
				length: "",
				earringThickness: "",
				wireThickness: "",
				stockLocation: "",
				imageStatus: "pending",
				productStatus: "active",
				gender: "",
				stoneType: "",
				isNewProduct: false,
				isOnSale: false,
				inStock: true,
				isFeatured: false,
				quantity: "",
				originalPrice: "",
			});
		} catch {
			console.log("error creating a product");
		}
	};

	const handleImageChange = (e) => {
		const files = Array.from(e.target.files);
		const remainingSlots = 5 - newProduct.images.length;
		const filesToAdd = files.slice(0, remainingSlots);

		filesToAdd.forEach(file => {
			const reader = new FileReader();
			reader.onloadend = () => {
				setNewProduct(prev => ({
					...prev,
					images: [...prev.images, reader.result]
				}));
			};
			reader.readAsDataURL(file);
		});
	};

	const removeImage = (index) => {
		setNewProduct(prev => ({
			...prev,
			images: prev.images.filter((_, i) => i !== index)
		}));
	};

	return (
		<motion.div
			className='bg-brand-primary-light shadow-lg rounded-lg p-8 mb-8 max-w-7xl mx-auto'
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.8 }}
		>
			<h2 className='text-2xl font-semibold mb-6 text-gray-800'>יצירת מוצר חדש</h2>

			<form onSubmit={handleSubmit} className='space-y-4'>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					<div>
						<label htmlFor='name' className='block text-sm font-medium text-gray-500'>
							שם המוצר *
						</label>
						<input
							type='text'
							id='name'
							name='name'
							value={newProduct.name}
							onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2
							 px-3 text-white focus:outline-none focus:ring-2
							focus:ring-emerald-500 focus:border-emerald-500'
							required
						/>
					</div>

					<div>
						<label htmlFor='sku' className='block text-sm font-medium text-gray-500'>
							SKU (אוטומטי)
						</label>
						<input
							type='text'
							id='sku'
							name='sku'
							value={newProduct.sku}
							onChange={(e) => setNewProduct({ ...newProduct, sku: e.target.value })}
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2
							 px-3 text-white focus:outline-none focus:ring-2
							focus:ring-emerald-500 focus:border-emerald-500'
							readOnly
						/>
						<p className='text-xs text-gray-500 mt-1'>
							{newProduct.category === 'necklaces' ? 'SHA-N-XXX' : 
							 newProduct.category === 'earrings' ? 'SHA-E-XXX' :
							 newProduct.category === 'bracelets' ? 'SHA-B-XXX' :
							 newProduct.category === 'rings' ? 'SHA-R-XXX' : 'בחר קטגוריה'}
						</p>
					</div>
				</div>

				<div>
					<label htmlFor='description' className='block text-sm font-medium text-gray-500'>
						תיאור *
					</label>
					<textarea
						id='description'
						name='description'
						value={newProduct.description}
						onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
						rows='3'
						className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm
						 py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 
						 focus:border-emerald-500'
						required
					/>
				</div>

				<div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
					<div>
						<label htmlFor='price' className='block text-sm font-medium text-gray-500'>
							מחיר *
						</label>
						<input
							type='number'
							id='price'
							name='price'
							value={newProduct.price}
							onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
							step='0.01'
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm 
							py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500
							 focus:border-emerald-500'
							required
						/>
					</div>

					<div>
						<label htmlFor='originalPrice' className='block text-sm font-medium text-gray-500'>
							מחיר מקורי
						</label>
						<input
							type='number'
							id='originalPrice'
							name='originalPrice'
							value={newProduct.originalPrice}
							onChange={(e) => setNewProduct({ ...newProduct, originalPrice: e.target.value })}
							step='0.01'
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm 
							py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500
							 focus:border-emerald-500'
						/>
					</div>

					<div>
						<label htmlFor='quantity' className='block text-sm font-medium text-gray-500'>
							כמות במלאי
						</label>
						<input
							type='number'
							id='quantity'
							name='quantity'
							value={newProduct.quantity}
							onChange={(e) => setNewProduct({ ...newProduct, quantity: e.target.value })}
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm 
							py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500
							 focus:border-emerald-500'
						/>
					</div>
				</div>

				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					<div>
						<label htmlFor='category' className='block text-sm font-medium text-gray-500'>
							קטגוריה *
						</label>
						<select
							id='category'
							name='category'
							value={newProduct.category}
							onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md
							 shadow-sm py-2 px-3 text-white focus:outline-none 
							 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500'
							required
						>
							<option value=''>בחר קטגוריה</option>
							{categories.map((category) => (
								<option key={category} value={category}>
									{category === 'necklaces' ? 'שרשראות' : category === 'earrings' ? 'עגילים' : category}
								</option>
							))}
						</select>
					</div>

					<div>
						<label htmlFor='gender' className='block text-sm font-medium text-gray-500'>
							מגדר
						</label>
						<select
							id='gender'
							name='gender'
							value={newProduct.gender}
							onChange={(e) => setNewProduct({ ...newProduct, gender: e.target.value })}
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md
							 shadow-sm py-2 px-3 text-white focus:outline-none 
							 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500'
						>
							<option value=''>בחר מגדר</option>
							<option value='נשים'>נשים</option>
							<option value='גברים'>גברים</option>
							<option value='ילדים'>ילדים</option>
							<option value='אוניברסלי'>אוניברסלי</option>
						</select>
					</div>
				</div>

				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					<div>
						<label htmlFor='material' className='block text-sm font-medium text-gray-500'>
							חומר גלם
						</label>
						<input
							type='text'
							id='material'
							name='material'
							value={newProduct.material}
							onChange={(e) => setNewProduct({ ...newProduct, material: e.target.value })}
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2
							 px-3 text-white focus:outline-none focus:ring-2
							focus:ring-emerald-500 focus:border-emerald-500'
						/>
					</div>

					<div>
						<label htmlFor='stoneType' className='block text-sm font-medium text-gray-500'>
							סוג אבן
						</label>
						<input
							type='text'
							id='stoneType'
							name='stoneType'
							value={newProduct.stoneType}
							onChange={(e) => setNewProduct({ ...newProduct, stoneType: e.target.value })}
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2
							 px-3 text-white focus:outline-none focus:ring-2
							focus:ring-emerald-500 focus:border-emerald-500'
						/>
					</div>
				</div>

				<div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
					<div>
						<label htmlFor='weight' className='block text-sm font-medium text-gray-500'>
							משקל (גרם)
						</label>
						<input
							type='number'
							id='weight'
							name='weight'
							value={newProduct.weight}
							onChange={(e) => setNewProduct({ ...newProduct, weight: e.target.value })}
							step='0.1'
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm 
							py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500
							 focus:border-emerald-500'
						/>
					</div>

					<div>
						<label htmlFor='length' className='block text-sm font-medium text-gray-500'>
							אורך (ס&apos;מ)
						</label>
						<input
							type='number'
							id='length'
							name='length'
							value={newProduct.length}
							onChange={(e) => setNewProduct({ ...newProduct, length: e.target.value })}
							step='0.1'
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm 
							py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500
							 focus:border-emerald-500'
						/>
					</div>

					<div>
							<label htmlFor='earringThickness' className='block text-sm font-medium text-gray-500'>
							עובי עגיל (מ&apos;מ)
						</label>
						<input
							type='number'
							id='earringThickness'
							name='earringThickness'
							value={newProduct.earringThickness}
							onChange={(e) => setNewProduct({ ...newProduct, earringThickness: e.target.value })}
							step='0.1'
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm 
							py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500
							 focus:border-emerald-500'
						/>
					</div>

					<div>
						<label htmlFor='wireThickness' className='block text-sm font-medium text-gray-500'>
							עובי חוט (מ&apos;מ)
						</label>
						<input
							type='number'
							id='wireThickness'
							name='wireThickness'
							value={newProduct.wireThickness}
							onChange={(e) => setNewProduct({ ...newProduct, wireThickness: e.target.value })}
							step='0.1'
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm 
							py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500
							 focus:border-emerald-500'
						/>
					</div>
				</div>

				<div>
					<label htmlFor='stockLocation' className='block text-sm font-medium text-gray-500'>
						מיקום במלאי
					</label>
					<input
						type='text'
						id='stockLocation'
						name='stockLocation'
						value={newProduct.stockLocation}
						onChange={(e) => setNewProduct({ ...newProduct, stockLocation: e.target.value })}
						className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2
						 px-3 text-white focus:outline-none focus:ring-2
						focus:ring-emerald-500 focus:border-emerald-500'
					/>
				</div>

				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					<div>
						<label htmlFor='productStatus' className='block text-sm font-medium text-gray-500'>
							סטטוס מוצר
						</label>
						<select
							id='productStatus'
							name='productStatus'
							value={newProduct.productStatus}
							onChange={(e) => setNewProduct({ ...newProduct, productStatus: e.target.value })}
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md
							 shadow-sm py-2 px-3 text-white focus:outline-none 
							 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500'
						>
							<option value='active'>פעיל</option>
							<option value='inactive'>לא פעיל</option>
							<option value='discontinued'>הופסק</option>
						</select>
					</div>

					<div>
						<label htmlFor='imageStatus' className='block text-sm font-medium text-gray-500'>
							סטטוס תמונה
						</label>
						<select
							id='imageStatus'
							name='imageStatus'
							value={newProduct.imageStatus}
							onChange={(e) => setNewProduct({ ...newProduct, imageStatus: e.target.value })}
							className='mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md
							 shadow-sm py-2 px-3 text-white focus:outline-none 
							 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500'
						>
							<option value='pending'>ממתין</option>
							<option value='uploaded'>הועלה</option>
							<option value='processing'>מעובד</option>
							<option value='error'>שגיאה</option>
						</select>
					</div>
				</div>

				<div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
					<div className='flex items-center'>
						<input
							type='checkbox'
												id='isNewProduct'
					name='isNewProduct'
					checked={newProduct.isNewProduct}
					onChange={(e) => setNewProduct({ ...newProduct, isNewProduct: e.target.checked })}
					className='h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded'
				/>
				<label htmlFor='isNewProduct' className='ml-2 mr-1 block text-md font-medium text-gray-500'>
							מוצר חדש
						</label>
					</div>

					<div className='flex items-center'>
						<input
							type='checkbox'
							id='isOnSale'
							name='isOnSale'
							checked={newProduct.isOnSale}
							onChange={(e) => setNewProduct({ ...newProduct, isOnSale: e.target.checked })}
							className='h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded'
						/>
						<label htmlFor='isOnSale' className='ml-2 mr-1 block text-md font-medium text-gray-500'>
							במבצע
						</label>
					</div>

					<div className='flex items-center'>
						<input
							type='checkbox'
							id='inStock'
							name='inStock'
							checked={newProduct.inStock}
							onChange={(e) => setNewProduct({ ...newProduct, inStock: e.target.checked })}
							className='h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded'
						/>
						<label htmlFor='inStock' className='ml-2 mr-1 block text-md font-medium text-gray-500'>
							במלאי
						</label>
					</div>

					<div className='flex items-center'>
						<input
							type='checkbox'
							id='isFeatured'
							name='isFeatured'
							checked={newProduct.isFeatured}
							onChange={(e) => setNewProduct({ ...newProduct, isFeatured: e.target.checked })}
							className='h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded'
						/>
						<label htmlFor='isFeatured' className='ml-2 mr-1 block text-md font-medium text-gray-500'>
							מומלץ
						</label>
					</div>
				</div>

				{/* העלאת תמונות */}
				<div className='space-y-4'>
					<div className='flex items-center justify-between'>
						<label className='block text-md font-medium text-brand-primary-hover'>
							תמונות מוצר (עד 5 תמונות)
						</label>
						<span className='text-sm text-gray-500'>
							{newProduct.images.length}/5
						</span>
					</div>
					
					{/* כפתור העלאה */}
					{newProduct.images.length < 5 && (
						<div className='mt-1 flex items-center'>
							<input 
								type='file' 
								id='images' 
								className='sr-only' 
								accept='image/*' 
								multiple
								onChange={handleImageChange} 
							/>
							<label
								htmlFor='images'
							    className='cursor-pointer bg-blue-900 py-2 px-3 border border-gray-600 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-100 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500'
							>
								<Upload className='h-5 w-5 inline-block mr-2 ml-2' />
								העלאת תמונות
							</label>
						</div>
					)}

					{/* תצוגת תמונות שהועלו */}
					{newProduct.images.length > 0 && (
						<div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-4'>
							{newProduct.images.map((image, index) => (
								<div key={index} className='relative group'>
									<img
										src={image}
										alt={`תמונה ${index + 1}`}
										className='w-full h-24 object-cover rounded-lg border border-gray-600'
									/>
									<button
										type='button'
										onClick={() => removeImage(index)}
										className='absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100'
									>
										<X className='h-4 w-4' />
									</button>
								</div>
							))}
						</div>
					)}
				</div>

				<button
					type='submit'
					className='w-1/2 object-center mx-auto flex justify-center py-2 px-4 border border-transparent rounded-md 
					shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 
					focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50'
					disabled={loading}
				>
					{loading ? (
						<>
							<Loader className='mr-2 h-5 w-5 animate-spin' aria-hidden='true' />
							Loading...
						</>
					) : (
						<>
							<PlusCircle className='mr-2 h-6 w-6 ml-1' />
							יצירת מוצר
						</>
					)}
				</button>
			</form>
		</motion.div>
	);
};
export default CreateProductForm;
